import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils";

/**
 * One row of Deal Capture's Explore tree: a link (`to`) or a jump link
 * (`onJump`, which scrolls the open page and goes nowhere). Either one then
 * calls `onClose`, which shuts the mobile drawer. Styled as the
 * shared sidebar's sub-rows (`NavSubLeaf`), with an optional count at the
 * right.
 */
export function ExploreRow({
  label,
  count,
  icon: Icon,
  to,
  onJump,
  onClose,
  active = false,
  indent = false,
}: {
  label: string;
  count?: string;
  icon?: LucideIcon;
  to?: string;
  onJump?: () => void;
  /** Closes the mobile drawer after the row acts. */
  onClose?: () => void;
  active?: boolean;
  /** One level further in: a jump link under its group. */
  indent?: boolean;
}) {
  const className = cn(
    "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    indent && "pl-6",
    active
      ? "bg-nav-active-bg font-semibold text-primary"
      : "text-muted-foreground hover:bg-row-hover hover:text-foreground",
  );
  const body = (
    <>
      {Icon && <Icon size={14} strokeWidth={1.75} className="shrink-0" />}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined && (
        <span className="shrink-0 tabular-nums text-text-dim">{count}</span>
      )}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        onClick={onClose}
        className={className}
        aria-current={active ? "page" : undefined}
      >
        {body}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        onJump?.();
        onClose?.();
      }}
      className={className}
    >
      {body}
    </button>
  );
}
